import React, { useState, useEffect } from 'react';
import { Calendar, Users, MapPin, Clock, User, Check, X, Euro, AlertCircle, Search, Heart, Send } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NewEventModal from '../components/NewEventModal';
import AutoMatchesModal from '../components/AutoMatchesModal';
import ViewClientsModal from '../components/ViewClientsModal';
import EventDetailModal from '../components/EventDetailModal';
import { eventApi, eventParticipantsApi } from '../lib/api';
import type { Tables } from '../lib/types';
import { withCache, Cache } from '../lib/cache';
import { supabase } from '../lib/supabase';

const Events = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAutoMatchModalOpen, setIsAutoMatchModalOpen] = useState(false);
  const [autoMatchEventId, setAutoMatchEventId] = useState<string | null>(null);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [events, setEvents] = useState<Tables<'events'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [allProfiles, setAllProfiles] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [detailEvent, setDetailEvent] = useState<Tables<'events'> | null>(null);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\./g, '/');
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  useEffect(() => {
    if ((user?.role === 'organizer' || user?.role === 'pr') && !user?.city) {
      setError('Città non definita. Vai al tuo profilo per impostare la città.');
      setLoading(false);
      return;
    }
    loadEvents();
    if (user?.role === 'admin') {
      loadProfiles();
    }

  }, [location, user]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const eventsData = await withCache(
        `events_${user?.role}_${user?.city || 'all'}_${user?.id || 'none'}`,
        async () => {
          const data = await eventApi.getAll();

          if (user?.role === 'organizer' || user?.role === 'pr') {
            return data.filter(event => event.city === user.city);
          } else if (user?.role === 'collaboratore') {
            return data.filter(event => (event as any).collaborator_id === user.id);
          }
          return data;
        },
        30000
      );

      setEvents(eventsData);

      const { state } = location;
      if (state?.selectedEventId && state?.openParticipantsModal) {
        const selectedEvent = eventsData.find(
          (event) => event.id === state.selectedEventId
        );
        if (selectedEvent) {
          setSelectedEvent(selectedEvent);
          setIsParticipantsModalOpen(true);
        }
      }
    } catch (error: any) {
      console.error('Error loading events:', error);
      setError(error.message || 'Errore nel caricamento degli eventi');
    } finally {
      setLoading(false);
    }
  };

  const loadProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, email, role')
        .eq('role', 'collaboratore')
        .order('name', { ascending: true });

      if (error) throw error;
      setAllProfiles(data || []);
    } catch (error: any) {
      console.error('Error loading profiles:', error);
    }
  };

  const handleNewEvent = async (eventData: any) => {
    try {
      const eventPayload: any = {
        name: eventData.name,
        age_range: eventData.ageRange,
        date: eventData.date,
        time: eventData.time,
        location: eventData.location,
        max_participants: parseInt(eventData.maxParticipants),
        men_price: parseFloat(eventData.menPrice),
        women_price: parseFloat(eventData.womenPrice),
        status: 'in programma',
        city: user?.role === 'admin' ? eventData.city : user?.city,
        image_url: eventData.imageUrl || null
      };

      if (eventData.collaboratorId) {
        eventPayload.collaborator_id = eventData.collaboratorId;
      }

      if (editingEvent) {
        await eventApi.update(editingEvent.id, eventPayload);
      } else {
        await eventApi.create(eventPayload);
      }

      await loadEvents();
      setIsModalOpen(false);
      setEditingEvent(null);
    } catch (error: any) {
      console.error('Error saving event:', error);
      setError(error.message || 'Errore nel salvare l\'evento');
    }
  };

  const handleAddParticipant = (eventId: string, participantCount: number) => {
    setEvents(prevEvents => prevEvents.map(event => 
      event.id === eventId 
        ? { ...event, participant_count: participantCount }
        : event
    ));
  };

  const handleRemoveParticipant = async (eventId: string, participantId: string) => {
    try {
      await eventParticipantsApi.remove(eventId, participantId);
      
      // Update the participant count in the local state
      const participants = await eventParticipantsApi.getByEventId(eventId);
      setEvents(prevEvents => prevEvents.map(event => 
        event.id === eventId 
          ? { ...event, participant_count: participants.length }
          : event
      ));
    } catch (error: any) {
      console.error('Error removing participant:', error);
      setError('Errore durante la rimozione del partecipante');
    }
  };

  const handleEdit = (event: Tables<'events'>) => {
    setEditingEvent(event);
    setIsModalOpen(true);
  };

  const handleDelete = async (eventId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
      try {
        setLoading(true);
        await eventApi.delete(eventId);
        setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
        const cache = Cache.getInstance();
        cache.invalidate(`events_${user?.role}_${user?.city || 'all'}_${user?.id || 'none'}`);
        cache.invalidate('homepage_events');
        cache.invalidate('homepage_stats');
        cache.invalidate('homepage_event_stats');
      } catch (error: any) {
        console.error('Error deleting event:', error);
        setError(error.message || 'Errore nell\'eliminazione dell\'evento');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (eventId: string, newStatus: 'pending' | 'ultimi_posti' | 'completo' | 'lista_attesa') => {
    try {
      // Immediately update the UI
      setEvents(prevEvents => prevEvents.map(event =>
        event.id === eventId
          ? { ...event, status: newStatus }
          : event
      ));

      // Then update the backend
      await eventApi.update(eventId, { status: newStatus });

      // Invalidate all event-related caches to ensure immediate updates across all pages
      const cache = Cache.getInstance();
      cache.invalidate(`events_${user?.role}_${user?.city || 'all'}_${user?.id || 'none'}`);
      cache.invalidate('homepage_events');
      cache.invalidate('homepage_stats');
      cache.invalidate('homepage_event_stats');
    } catch (error: any) {
      console.error('Error updating event status:', error);
      setError(error.message || 'Errore nell\'aggiornamento dello stato');

      // Revert UI if the update failed
      await loadEvents();
    }
  };

  const handleCollaboratorChange = async (eventId: string, collaboratorId: string) => {
    try {
      const collaboratorIdToUpdate = collaboratorId === '' ? null : collaboratorId;

      // Immediately update the UI
      setEvents(prevEvents => prevEvents.map(event => {
        if (event.id === eventId) {
          const collaborator = collaboratorIdToUpdate
            ? allProfiles.find(p => p.id === collaboratorIdToUpdate)
            : null;
          return {
            ...event,
            collaborator_id: collaboratorIdToUpdate,
            collaborator: collaborator || null
          };
        }
        return event;
      }));

      // Then update the backend
      await eventApi.update(eventId, { collaborator_id: collaboratorIdToUpdate });
    } catch (error: any) {
      console.error('Error updating collaborator:', error);
      setError(error.message || 'Errore nell\'aggiornamento del collaboratore');

      // Revert UI if the update failed
      await loadEvents();
    }
  };

  const handleToggleMatchesSent = async (eventId: string, currentValue: boolean) => {
    const newValue = !currentValue;
    const confirmMsg = newValue
      ? 'Vuoi inviare i match? I partecipanti non potranno più modificare le preferenze e potranno vedere i loro match.'
      : 'Vuoi riaprire le preferenze? I partecipanti potranno di nuovo modificare le preferenze e non vedranno più i match.';
    if (!window.confirm(confirmMsg)) return;

    try {
      setEvents(prevEvents => prevEvents.map(event =>
        event.id === eventId ? { ...event, matches_sent: newValue } : event
      ));

      const { error } = await supabase
        .from('events')
        .update({ matches_sent: newValue })
        .eq('id', eventId);

      if (error) throw error;

      const cache = Cache.getInstance();
      cache.invalidate(`events_${user?.role}_${user?.city || 'all'}_${user?.id || 'none'}`);
    } catch (error: any) {
      console.error('Error toggling matches_sent:', error);
      setEvents(prevEvents => prevEvents.map(event =>
        event.id === eventId ? { ...event, matches_sent: currentValue } : event
      ));
      setError(error.message || 'Errore nell\'invio dei match');
    }
  };

  const handleViewParticipants = (event: Tables<'events'>) => {
    setSelectedEvent(event);
    setIsParticipantsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEvent(null);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Eventi</h1>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  const filteredEvents = events.filter(event => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      event.name.toLowerCase().includes(searchLower) ||
      event.location.toLowerCase().includes(searchLower) ||
      event.city.toLowerCase().includes(searchLower) ||
      event.age_range.toLowerCase().includes(searchLower) ||
      formatDate(event.date).includes(searchLower)
    );
  });

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const upcomingFilteredEvents = filteredEvents
    .filter(event => new Date(event.date) >= now)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const pastFilteredEvents = filteredEvents
    .filter(event => new Date(event.date) < now)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const totalEvents = events.length;
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate >= new Date();
  }).length;
  const confirmedEvents = events.filter(event => event.status === 'confirmed').length;
  const totalParticipants = events.reduce((sum, event) => sum + (event.participant_count || 0), 0);

  const nextEvent = events
    .filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= new Date();
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Eventi</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalEvents}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Eventi Prossimi</p>
                  <p className="text-3xl font-bold text-white mt-1">{upcomingEvents}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Clock className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Data Prossimo Evento</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {nextEvent ? formatDate(nextEvent.date) : '-'}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Confermati</p>
                  <p className="text-3xl font-bold text-white mt-1">{confirmedEvents}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Check className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Partecipanti Totali</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalParticipants}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Cerca eventi per nome, location, città, fascia d'età o data..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-rose-500 focus:border-rose-500"
          />
        </div>
        {user?.role !== 'pr' && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-gradient-to-br from-rose-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all flex items-center gap-2 text-sm font-medium shadow-lg"
          >
            <Calendar className="h-4 w-4" />
            Nuovo Evento
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      {upcomingFilteredEvents.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-green-600" />
              Prossimi Eventi ({upcomingFilteredEvents.length})
            </h2>
          </div>
          <div className="p-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Nome Evento</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Data</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Ora</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Location</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Città</th>
                  {user?.role === 'admin' && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Organizzatore</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Collaboratore</th>
                    </>
                  )}
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Partecipanti</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                  {user?.role !== 'pr' && (
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Azioni</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {upcomingFilteredEvents.map((event) => (
                  <tr key={event.id} className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <button
                        onClick={() => setDetailEvent(event)}
                        className="flex items-center gap-2 text-left hover:text-rose-600 transition-colors group"
                      >
                        <span className="font-medium group-hover:underline">{event.name}</span>
                        <span className="text-sm text-gray-500">({event.age_range})</span>
                      </button>
                    </td>
                    <td className="py-3 px-4">{formatDate(event.date)}</td>
                    <td className="py-3 px-4">{formatTime(event.time)}</td>
                    <td className="py-3 px-4">{event.location}</td>
                    <td className="py-3 px-4">{event.city}</td>
                    {user?.role === 'admin' && (
                      <>
                        <td className="py-3 px-4">
                          {(event as any).creator?.name || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <select
                            value={(event as any).collaborator_id || ''}
                            onChange={(e) => handleCollaboratorChange(event.id, e.target.value)}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-rose-500 focus:border-rose-500"
                          >
                            <option value="">Nessuno</option>
                            {allProfiles.map(profile => (
                              <option key={profile.id} value={profile.id}>
                                {profile.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </>
                    )}
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleViewParticipants(event)}
                        className="flex items-center gap-2 text-rose-600 hover:text-rose-700"
                      >
                        <User className="h-4 w-4" />
                        <span>{event.participant_count || 0} / {event.max_participants}</span>
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      {user?.role !== 'pr' ? (
                        <div className="relative group">
                          <button
                            className={`px-2 py-1 rounded-full text-xs inline-flex items-center gap-1 ${
                              event.status === 'sold-out + lista attesa'
                                ? 'bg-red-100 text-red-800'
                                : event.status === 'quasi completo'
                                ? 'bg-orange-100 text-orange-800'
                                : event.status === 'posti limitati'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {event.status === 'sold-out + lista attesa' ? 'Sold-out + Lista Attesa' :
                             event.status === 'quasi completo' ? 'Quasi Completo' :
                             event.status === 'posti limitati' ? 'Posti Limitati' : 'In Programma'}
                          </button>
                          <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                            <div className="p-2 space-y-1">
                              <button
                                onClick={() => handleStatusChange(event.id, 'in programma')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-green-50 text-green-800 flex items-center gap-2"
                              >
                                <Calendar className="h-4 w-4" />
                                In Programma
                              </button>
                              <button
                                onClick={() => handleStatusChange(event.id, 'posti limitati')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-yellow-50 text-yellow-800 flex items-center gap-2"
                              >
                                <AlertCircle className="h-4 w-4" />
                                Posti Limitati
                              </button>
                              <button
                                onClick={() => handleStatusChange(event.id, 'quasi completo')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-orange-50 text-orange-800 flex items-center gap-2"
                              >
                                <AlertCircle className="h-4 w-4" />
                                Quasi Completo
                              </button>
                              <button
                                onClick={() => handleStatusChange(event.id, 'sold-out + lista attesa')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-red-50 text-red-800 flex items-center gap-2"
                              >
                                <Users className="h-4 w-4" />
                                Sold-out + Lista Attesa
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          event.status === 'sold-out + lista attesa'
                            ? 'bg-red-100 text-red-800'
                            : event.status === 'quasi completo'
                            ? 'bg-orange-100 text-orange-800'
                            : event.status === 'posti limitati'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {event.status === 'sold-out + lista attesa' ? 'Sold-out + Lista Attesa' :
                           event.status === 'quasi completo' ? 'Quasi Completo' :
                           event.status === 'posti limitati' ? 'Posti Limitati' : 'In Programma'}
                        </span>
                      )}
                    </td>
                    {user?.role !== 'pr' && (
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setAutoMatchEventId(event.id); setIsAutoMatchModalOpen(true); }}
                            className="text-amber-600 hover:text-amber-700 p-1 hover:bg-amber-50 rounded"
                            title="Mostra Match"
                          >
                            <Heart className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleToggleMatchesSent(event.id, !!(event as any).matches_sent)}
                            className={`p-1 rounded ${(event as any).matches_sent ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                            title={(event as any).matches_sent ? 'Match inviati (clicca per riaprire)' : 'Invia Match'}
                          >
                            <Send className="h-5 w-5" />
                          </button>
                          {user?.role === 'admin' && (
                            <>
                              <button
                                onClick={() => handleEdit(event)}
                                className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                                title="Modifica"
                              >
                                <Clock className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(event.id)}
                                className="text-rose-600 hover:text-rose-700 p-1 hover:bg-rose-50 rounded"
                                title="Elimina"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pastFilteredEvents.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <button
            onClick={() => setShowPastEvents(!showPastEvents)}
            className="w-full px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50 hover:from-gray-100 hover:to-slate-100 transition-colors"
          >
            <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-600" />
                Eventi Passati ({pastFilteredEvents.length})
              </span>
              <span className="text-gray-500">
                {showPastEvents ? '▲' : '▼'}
              </span>
            </h2>
          </button>
          {showPastEvents && (
            <div className="p-6">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Nome Evento</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Ora</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Città</th>
                    {user?.role === 'admin' && (
                      <>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Organizzatore</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Collaboratore</th>
                      </>
                    )}
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Partecipanti</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                    {user?.role !== 'pr' && (
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Azioni</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pastFilteredEvents.map((event) => (
                    <tr key={event.id} className="border-b border-gray-100 opacity-75">
                    <td className="py-3 px-4">
                      <button
                        onClick={() => setDetailEvent(event)}
                        className="flex items-center gap-2 text-left hover:text-rose-600 transition-colors group"
                      >
                        <span className="font-medium group-hover:underline">{event.name}</span>
                        <span className="text-sm text-gray-500">({event.age_range})</span>
                      </button>
                    </td>
                    <td className="py-3 px-4">{formatDate(event.date)}</td>
                    <td className="py-3 px-4">{formatTime(event.time)}</td>
                    <td className="py-3 px-4">{event.location}</td>
                    <td className="py-3 px-4">{event.city}</td>
                    {user?.role === 'admin' && (
                      <>
                        <td className="py-3 px-4">
                          {(event as any).creator?.name || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <select
                            value={(event as any).collaborator_id || ''}
                            onChange={(e) => handleCollaboratorChange(event.id, e.target.value)}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-rose-500 focus:border-rose-500"
                          >
                            <option value="">Nessuno</option>
                            {allProfiles.map(profile => (
                              <option key={profile.id} value={profile.id}>
                                {profile.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </>
                    )}
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleViewParticipants(event)}
                        className="flex items-center gap-2 text-rose-600 hover:text-rose-700"
                      >
                        <User className="h-4 w-4" />
                        <span>{event.participant_count || 0} / {event.max_participants}</span>
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      {user?.role !== 'pr' ? (
                        <div className="relative group">
                          <button
                            className={`px-2 py-1 rounded-full text-xs inline-flex items-center gap-1 ${
                              event.status === 'sold-out + lista attesa'
                                ? 'bg-red-100 text-red-800'
                                : event.status === 'quasi completo'
                                ? 'bg-orange-100 text-orange-800'
                                : event.status === 'posti limitati'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {event.status === 'sold-out + lista attesa' ? 'Sold-out + Lista Attesa' :
                             event.status === 'quasi completo' ? 'Quasi Completo' :
                             event.status === 'posti limitati' ? 'Posti Limitati' : 'In Programma'}
                          </button>
                          <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                            <div className="p-2 space-y-1">
                              <button
                                onClick={() => handleStatusChange(event.id, 'in programma')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-green-50 text-green-800 flex items-center gap-2"
                              >
                                <Calendar className="h-4 w-4" />
                                In Programma
                              </button>
                              <button
                                onClick={() => handleStatusChange(event.id, 'posti limitati')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-yellow-50 text-yellow-800 flex items-center gap-2"
                              >
                                <AlertCircle className="h-4 w-4" />
                                Posti Limitati
                              </button>
                              <button
                                onClick={() => handleStatusChange(event.id, 'quasi completo')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-orange-50 text-orange-800 flex items-center gap-2"
                              >
                                <AlertCircle className="h-4 w-4" />
                                Quasi Completo
                              </button>
                              <button
                                onClick={() => handleStatusChange(event.id, 'sold-out + lista attesa')}
                                className="w-full text-left px-3 py-1 text-sm rounded hover:bg-red-50 text-red-800 flex items-center gap-2"
                              >
                                <Users className="h-4 w-4" />
                                Sold-out + Lista Attesa
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          event.status === 'sold-out + lista attesa'
                            ? 'bg-red-100 text-red-800'
                            : event.status === 'quasi completo'
                            ? 'bg-orange-100 text-orange-800'
                            : event.status === 'posti limitati'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {event.status === 'sold-out + lista attesa' ? 'Sold-out + Lista Attesa' :
                           event.status === 'quasi completo' ? 'Quasi Completo' :
                           event.status === 'posti limitati' ? 'Posti Limitati' : 'In Programma'}
                        </span>
                      )}
                    </td>
                    {user?.role !== 'pr' && (
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setAutoMatchEventId(event.id); setIsAutoMatchModalOpen(true); }}
                            className="text-amber-600 hover:text-amber-700 p-1 hover:bg-amber-50 rounded"
                            title="Mostra Match"
                          >
                            <Heart className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleToggleMatchesSent(event.id, !!(event as any).matches_sent)}
                            className={`p-1 rounded ${(event as any).matches_sent ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                            title={(event as any).matches_sent ? 'Match inviati (clicca per riaprire)' : 'Invia Match'}
                          >
                            <Send className="h-5 w-5" />
                          </button>
                          {user?.role === 'admin' && (
                            <>
                              <button
                                onClick={() => handleEdit(event)}
                                className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                                title="Modifica"
                              >
                                <Clock className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(event.id)}
                                className="text-rose-600 hover:text-rose-700 p-1 hover:bg-rose-50 rounded"
                                title="Elimina"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {filteredEvents.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6">
            <div className="text-center py-8">
              {searchTerm ? (
                <>
                  <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">
                    Nessun evento trovato per "{searchTerm}"
                  </p>
                </>
              ) : (
                <>
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">
                    {error || 'Nessun evento disponibile'}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <NewEventModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleNewEvent}
        initialData={editingEvent}
        allProfiles={allProfiles}
      />

      <AutoMatchesModal
        isOpen={isAutoMatchModalOpen}
        onClose={() => setIsAutoMatchModalOpen(false)}
        eventId={autoMatchEventId || '0'}
      />

      <ViewClientsModal
        isOpen={isParticipantsModalOpen}
        onClose={() => setIsParticipantsModalOpen(false)}
        event={selectedEvent}
        onRemoveParticipant={handleRemoveParticipant}
        onAddParticipant={handleAddParticipant}
      />

      <EventDetailModal
        isOpen={!!detailEvent}
        onClose={() => setDetailEvent(null)}
        event={detailEvent}
      />
    </div>
  );
};

export default Events;